import * as THREE from "three";
import * as d3 from "d3";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import { Line2 } from "three/examples/jsm/lines/Line2";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";

import {
  GeoJsonType,
  GeoJsonFeature,
  GeometryCoordinates,
  GeometryType,
  ExtendObject3D,
  ProjectionFnParamType,
} from "./typed";
import { mapConfig } from "./mapConfig";
import arrSvg from "../assets/img/arr.svg";

let flySpotTexture: THREE.Texture | null = null;

function getFlySpotTexture() {
  if (!flySpotTexture) {
    flySpotTexture = new THREE.TextureLoader().load(arrSvg);
    flySpotTexture.encoding = THREE.sRGBEncoding;
  }
  return flySpotTexture;
}

/**
 * 线上移动的箭头（使用 arr.svg，沿曲线切线朝向）
 * mapScale：父级地图缩放，用于保证各级视觉大小一致
 */
export const drawflySpot = (curve: any, mapScale: number) => {
  const texture = getFlySpotTexture();
  const size =
    mapConfig.flyArrowWorldSize / Math.max(mapScale, 0.001);
  const geometry = new THREE.PlaneGeometry(size, size);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh: any = new THREE.Mesh(geometry, material);
  mesh.curve = curve;
  mesh._curveLength = Math.max(curve.getLength(), 0.001);
  mesh._mapScale = Math.max(mapScale, 0.001);
  mesh._s = 0;
  // SVG 箭头朝向 +X，动画里对齐曲线切线
  mesh._tangentAxis = new THREE.Vector3(1, 0, 0);
  return mesh;
};

export function getDynamicMapScale(
  mapObject3D: THREE.Object3D,
  containerRef: any
) {
  // const width = containerRef.offsetWidth;
  // const height = containerRef.offsetHeight;
  const width = containerRef.clientWidth;
  const height = containerRef.clientHeight;
  const refArea = width * height;

  const boundingBox = new THREE.Box3().setFromObject(mapObject3D);
  // 获取包围盒的尺寸
  const size = new THREE.Vector3();
  boundingBox.getSize(size);
  // 新增 Math.random避免缩放为1，没有动画效果
  const scale =
    Math.round(Math.sqrt(refArea / (size.x * size.y * 400))) +
    parseFloat((Math.random() + 0.5).toFixed(2));
  return scale;
}

// 绘制挤出的材质
export function drawExtrudeMesh(
  point: [number, number][],
  projectionFn: any
): any {
  const shape = new THREE.Shape();
  const pointsArray = [];

  for (let i = 0; i < point.length; i++) {
    const [x, y]: any = projectionFn(point[i]); // 将每一个经纬度转化为坐标点
    if (i === 0) {
      shape.moveTo(x, -y);
    }
    shape.lineTo(x, -y);
    pointsArray.push(x, -y, mapConfig.topLineZIndex);
  }

  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: mapConfig.mapDepth, // 挤出的形状深度
    bevelEnabled: false, // 对挤出的形状应用是否斜角
  });

  const material = new THREE.MeshPhongMaterial({
    // color: mapConfig.mapColor,
    color: mapConfig.mapColorGradient[Math.floor(Math.random() * 4)], // 随机颜色
    transparent: mapConfig.mapTransparent,
    opacity: mapConfig.mapOpacity,
    depthWrite: false,
  });

  const materialSide = new THREE.ShaderMaterial({
    uniforms: {
      color1: {
        value: new THREE.Color(mapConfig.mapSideColor1),
      },
      color2: {
        value: new THREE.Color(mapConfig.mapSideColor2),
      },
      uOpacity: {
        value: mapConfig.mapOpacity,
      },
    },
    transparent: mapConfig.mapTransparent,
    depthWrite: false,
    vertexShader: `
      varying vec3 vPosition;
      void main() {
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 color1;
      uniform vec3 color2;
      uniform float uOpacity;
      varying vec3 vPosition;
      void main() {
        vec3 mixColor = mix(color1, color2, 0.5 - vPosition.z * 0.2); // 使用顶点坐标 z 分量来控制混合
        gl_FragColor = vec4(mixColor, uOpacity);
      }
    `,
    //   wireframe: true,
  });

  const mesh: any = new THREE.Mesh(geometry, [material, materialSide]);
  // userData 存储自定义属性
  mesh.userData = {
    isChangeColor: true,
  };

  // 边框线，赋值空间点坐标，3个一组
  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(pointsArray);

  const lineMaterial = new LineMaterial({
    color: mapConfig.topLineColor,
    linewidth: mapConfig.topLineWidth,
  });
  lineMaterial.resolution.set(window.innerWidth, window.innerHeight);
  const line = new Line2(lineGeometry, lineMaterial);

  return { mesh, line };
}

// 生成地图3D模型
export function generateMapObject3D(
  mapdata: GeoJsonType,
  projectionFnParam: ProjectionFnParamType
) {
  // 地图对象
  const mapObject3D = new THREE.Object3D();
  // 地图数据
  const basicFeatures = mapdata?.features;
  if (!Array.isArray(basicFeatures) || basicFeatures.length === 0) {
    console.error("generateMapObject3D: invalid geojson", mapdata);
    throw new Error("地图数据无效：缺少 features");
  }

  const { center, scale } = projectionFnParam;

  const projectionFn = d3
    .geoMercator()
    .center(center)
    .scale(scale)
    .translate([0, 0]);

  const label2dData: any = []; // 存储自定义 2d 标签数据

  // 每个省的数据
  basicFeatures.forEach((basicFeatureItem: GeoJsonFeature) => {
    if (!basicFeatureItem?.geometry?.coordinates) {
      return;
    }
    // 每个省份的地图对象
    const provinceMapObject3D = new THREE.Object3D() as ExtendObject3D;
    // 将地图数据挂在到模型数据上
    provinceMapObject3D.customProperties = basicFeatureItem.properties;

    // 每个坐标类型
    const featureType = basicFeatureItem.geometry.type;
    // 每个坐标数组
    const featureCoords: GeometryCoordinates<GeometryType> =
      basicFeatureItem.geometry.coordinates;
    // 每个中心点位置（乡镇数据可能只有几何，无 centroid）
    const centerLngLat =
      basicFeatureItem.properties.centroid ||
      basicFeatureItem.properties.center;
    const featureCenterCoord: any =
      centerLngLat && projectionFn(centerLngLat);
    // 名字
    const featureName: string = basicFeatureItem.properties.name;

    if (featureCenterCoord) {
      label2dData.push({
        featureCenterCoord,
        featureName,
      });
    }

    // MultiPolygon 类型
    if (featureType === "MultiPolygon") {
      featureCoords.forEach((multiPolygon: [number, number][][]) => {
        multiPolygon.forEach((polygon: [number, number][]) => {
          const { mesh, line } = drawExtrudeMesh(polygon, projectionFn);
          provinceMapObject3D.add(mesh);
          provinceMapObject3D.add(line);
        });
      });
    }

    // Polygon 类型
    if (featureType === "Polygon") {
      featureCoords.forEach((polygon: [number, number][]) => {
        const { mesh, line } = drawExtrudeMesh(polygon, projectionFn);
        provinceMapObject3D.add(mesh);
        provinceMapObject3D.add(line);
      });
    }

    mapObject3D.add(provinceMapObject3D);
  });

  return { mapObject3D, label2dData };
}

// 生成地图2D标签
export function generateMapLabel2D(label2dData: any) {
  const labelObject2D = new THREE.Object3D();
  label2dData.forEach((item: any) => {
    const { featureCenterCoord, featureName } = item;
    const labelObjectItem = draw2dLabel(featureCenterCoord, featureName);
    if (labelObjectItem) {
      labelObject2D.add(labelObjectItem);
    }
  });
  return labelObject2D;
}

// 生成地图spot点位
export function generateMapSpot(label2dData: any) {
  const spotObject3D = new THREE.Object3D();
  const spotList: any = [];
  label2dData.forEach((item: any) => {
    const { featureCenterCoord } = item;
    const spotObjectItem = drawSpot(featureCenterCoord);
    if (spotObjectItem && spotObjectItem.circle && spotObjectItem.ring) {
      spotObject3D.add(spotObjectItem.circle);
      spotObject3D.add(spotObjectItem.ring);
      spotList.push(spotObjectItem.ring);
    }
  });
  return { spotObject3D, spotList };
}

// 绘制二维标签
export const draw2dLabel = (coord: [number, number], proviceName: string) => {
  if (coord && coord.length) {
    // 模版字符串
    const innerHTML = `<div class="your-classname" style="color: #fff">${proviceName}</div>`;
    const labelDiv = document.createElement("div");
    labelDiv.innerHTML = innerHTML;
    labelDiv.style.pointerEvents = "none"; // 禁用事件，否则tooltip悬浮在当前div会导致失去事件追踪
    const labelObject = new CSS2DObject(labelDiv);
    labelObject.position.set(coord[0], -coord[1], mapConfig.label2dZIndex);
    return labelObject;
  }
};

// 绘制圆点
export const drawSpot = (coord: [number, number]) => {
  if (coord && coord.length) {
    /**
     * 绘制圆点
     */
    const spotGeometry = new THREE.CircleGeometry(mapConfig.spotRadius, 200);
    const spotMaterial = new THREE.MeshBasicMaterial({
      color: "#3EC5FB",
      side: THREE.DoubleSide,
    });
    const circle = new THREE.Mesh(spotGeometry, spotMaterial);
    circle.position.set(coord[0], -coord[1], mapConfig.spotZIndex);

    // 圆环
    const ringGeometry = new THREE.RingGeometry(
      mapConfig.spotRadius,
      mapConfig.spotRadius + mapConfig.spotRingWidth,
      50
    );
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: "#3FC5FB",
      side: THREE.DoubleSide,
      transparent: true,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.set(coord[0], -coord[1], mapConfig.spotZIndex);
    return { circle, ring };
  }
};

// 按渐变色标采样颜色（t ∈ [0, 1]）
function sampleFlyLineColor(t: number, target: THREE.Color) {
  const stops = mapConfig.flyLineGradientStops;
  const clamped = Math.min(1, Math.max(0, t));

  for (let i = 0; i < stops.length - 1; i += 1) {
    const curr = stops[i];
    const next = stops[i + 1];
    if (clamped >= curr.offset && clamped <= next.offset) {
      const localT =
        (clamped - curr.offset) / (next.offset - curr.offset || 1);
      return target
        .setStyle(curr.color)
        .lerp(new THREE.Color(next.color), localT);
    }
  }

  return target.setStyle(stops[stops.length - 1].color);
}

// 绘制两点链接飞线
export const drawLineBetween2Spot = (
  coordStart: [number, number],
  coordEnd: [number, number],
  mapScale: number
) => {
  const [x0, y0, z0] = [...coordStart, mapConfig.spotZIndex];
  const [x1, y1, z1] = [...coordEnd, mapConfig.spotZIndex];
  // 使用 QuadraticBezierCurve3 创建 三维二次贝塞尔曲线
  const curve = new THREE.QuadraticBezierCurve3(
    new THREE.Vector3(x0, -y0, z0),
    new THREE.Vector3((x0 + x1) / 2, -(y0 + y1) / 2, 20),
    new THREE.Vector3(x1, -y1, z1)
  );

  const flySpot = drawflySpot(curve, mapScale);

  const lineGeometry = new THREE.BufferGeometry();
  // 获取曲线上50个点
  const points = curve.getPoints(50);
  const positions = [];
  const colors = [];
  const color = new THREE.Color();

  // 沿轨迹参数插值多段渐变色
  for (let j = 0; j < points.length; j++) {
    const t = points.length === 1 ? 0 : j / (points.length - 1);
    sampleFlyLineColor(t, color);
    colors.push(color.r, color.g, color.b);
    positions.push(points[j].x, points[j].y, points[j].z);
  }
  // 放入顶点 和 设置顶点颜色
  lineGeometry.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(positions), 3, true)
  );
  lineGeometry.setAttribute(
    "color",
    new THREE.BufferAttribute(new Float32Array(colors), 3, true)
  );

  const material = new THREE.LineBasicMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
  });
  const flyLine = new THREE.Line(lineGeometry, material);

  return { flyLine, flySpot };
};
